import { Type } from 'app/actions/form/reverse-mortgage/types';
import { RootState } from 'app/store/types';
import { createSelector } from 'reselect';

/**
 * hecmFailed/highTechFailed are tracked per side because the two calcs are requested together but
 * can succeed or fail independently; a failed side's numbers in the form are stale and
 * untrustworthy. hecmError/highTechError are pricing-engine's own description of why a failed
 * side failed (e.g. no plan for this age/payment option), and are undefined when that side didn't
 * fail or sent no description.
 */
type ReverseMortgageState = {
  isPending       : boolean;
  hecmFailed      : boolean;
  highTechFailed  : boolean;
  hecmError?      : string;
  highTechError?  : string;
};

const initialState: ReverseMortgageState = { isPending: false, hecmFailed: false, highTechFailed: false };

export const reverseMortgageReducer = (state = initialState, action): ReverseMortgageState => {
  switch (action.type) {
    case Type.AJAX_REVERSE_MORTGAGE_CALCS:
      return { ...state, isPending: true };

    // Success means at least one side came back; the payload says which (if any) did not.
    case Type.AJAX_REVERSE_MORTGAGE_CALCS_SUCCESS:
      return {
        isPending      : false,
        hecmFailed     : !!action.payload?.hecmFailed,
        highTechFailed : !!action.payload?.highTechFailed,
        hecmError      : action.payload?.hecmError,
        highTechError  : action.payload?.highTechError,
      };

    case Type.AJAX_REVERSE_MORTGAGE_CALCS_FAIL:
      return {
        isPending      : false,
        hecmFailed     : true,
        highTechFailed : true,
        hecmError      : action.payload?.hecmError,
        highTechError  : action.payload?.highTechError,
      };

    default:
      return state;
  }
};

export const reverseMortgageSelector = (state: RootState): ReverseMortgageState => state.reverseMortgage;
export const isReverseMortgageCalcsPending = createSelector([reverseMortgageSelector], (reverseMortgage) => reverseMortgage.isPending);
export const hasHecmCalcFailed = createSelector([reverseMortgageSelector], (reverseMortgage) => reverseMortgage.hecmFailed);
export const hasHighTechCalcFailed = createSelector([reverseMortgageSelector], (reverseMortgage) => reverseMortgage.highTechFailed);
export const getHecmCalcError = createSelector([reverseMortgageSelector], (reverseMortgage) => reverseMortgage.hecmError);
export const getHighTechCalcError = createSelector([reverseMortgageSelector], (reverseMortgage) => reverseMortgage.highTechError);
